<template>
  <v-container>
    <v-layout justify-center>
      <v-flex>
        <v-card class="elevation-12 mb-8">
          <v-app-bar dark color="primary">
            <img :src="qrIcon" class="qr-icon" />
            <span v-if="$store.getters.loyaltyScan.couponEnabled">{{$t('couponScan.couponScanInstruction')}}</span>
            <span v-else>{{$t('couponScan.rksvScanInstructions')}}</span>
          </v-app-bar>
          <v-card-text>
            <div class="qr-code-scanner-wrapper">
              <qr-code-scanner ref="qrCodeScanner" @codeScanned="readCode" />
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="exceptionDialog" max-width="330" persistent>
      <v-card>
        <v-card-title class="headline">{{exceptionTitle}}</v-card-title>
        <v-card-text v-html="exceptionMessage"></v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="cancel()">{{ $t('couponScan.errorDialogCancelButton') }}</v-btn>
          <v-btn text color="primary" @click="restartQrCodeScanner()">{{$t('couponScan.tryAgain')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <congrats-dialog v-if="congratsDialog" :response="congratsData" :showDialog="congratsDialog" @hideDialog="closeCongratsDialog" />

  </v-container>
</template>

<script>
  import _ from 'lodash';
  import {mapGetters} from 'vuex';
  import calculateWaitTime from '@/utils/calculateWaitTime';
  import QrCodeScanner from '@/components/loyaltyscanner/QrCodeScanner';
  import CongratsDialog from '@/components/loyaltyscanner/CongratsDialog';
  import {BASE_API_URL} from '@/store';
  import QR_CODE_ICON from '@/assets/qr-code.png'
  import {errorCodes, errorTitles} from '@/utils/errorCodes';
  import {convertResponseToError} from '@/utils/error_messages';

  export default {
    name: "RksvScan",
    components: {
      QrCodeScanner, CongratsDialog
    },
    data() {
      return {
        code: '',
        exceptionDialog: false,
        exceptionTitle: '',
        exceptionMessage: '',
        congratsDialog: false,
        congratsData: false,
        qrIcon: QR_CODE_ICON
      }
    },
    computed: {
      ...mapGetters(['extensionSettings'])
    },
    methods: {
      readCode(code) {
        if (_.get(this.extensionSettings, 'loyalty.rksv.enabled', false) && this.isRKSVBon(code)) { // RKSVBON
          this.redeemRKSVBon(code);
        } else if (_.get(this.extensionSettings, 'loyalty.coupon.enabled', false) && this.isCoupon(code) && this.isUrl(code)) { // COUPON
          const coupon = code.split('/')[6];
          this.redeemCoupon(coupon);
        } else {
          this.showExceptionDialog(this.$t('couponScan.errorTitle'), this.$t('couponScan.errorNotValidCode'));
        }
      },
      isUrl(code) {
        return /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)/g.test(code);
      },
      isRKSVBon(code) {
        const res = code.split('_');
        if (res.length === 14) {
          return true;
        }
        return false;
      },
      isCoupon(coupon) {
        const res = coupon.split('/');
        if (res.length !== 8) {
          return false;
        }
        return true;
      },
      async redeemRKSVBon(rawRksvBon) {
        let body = JSON.stringify({
          'receipt': rawRksvBon
        });

        try {
          let response = await this.axios.post(`${BASE_API_URL}/api/v1/clients/${this.$store.state.client_id}/rksv-redeem/`, body);

          this.redeemRKSVCallback(response)
        } catch (error) {
          console.error(error);
          this.showExceptionDialog(this.$t('couponScan.noteTitle'), this.$t('errorMessages.default'));
        }
      },
      redeemRKSVCallback(response) {
        if (_.get(response, 'data.success')) {
          this.congratsData = response.data;
          this.congratsDialog = true;
        } else {
          let errorTitle = convertResponseToError(response, errorTitles);
          if (!errorTitle) {
            errorTitle = 'couponScan.errorTitle';
          }
          let errorMessage = convertResponseToError(response, errorCodes);
          this.showExceptionDialog(this.$t(errorTitle), this.$t(errorMessage));
        }
      },
      redeemCoupon(coupon) {
        if (!_.isEmpty(coupon)) {
          this.sendRedeemCoupon(coupon);
        } else {
          this.showExceptionDialog(this.$t('couponScan.noteTitle'), this.$t('errorMessages.default'));
        }
      },
      async sendRedeemCoupon(coupon) {
        try {
          let response = await this.axios.post(`${BASE_API_URL}/api/v1/clients/${this.$store.state.client_id}/redeem/${coupon}/`);
          let data = response.data;

          if (data.success) {
            this.congratsData = data;
            this.congratsDialog = true;
          } else {
            let errorMessage;

            if (_.get(data, 'code') === 'COUPON_THROTTLE_LIMIT_REACHED') {
              if (_.get(data, 'wait', false)) {
                const {days, waitTime} = calculateWaitTime(_.get(data, 'wait', 0));
                errorMessage = this.$tc('couponScan.couponLimitReachedWithTimeText', days, {count: days, waitTime});
              } else {
                errorMessage = this.$t('couponScan.couponThrottleLimitReached');
              }
            } else {
              let msg = convertResponseToError(response, errorCodes);
              errorMessage = this.$t(msg);
            }

            this.showExceptionDialog(this.$t('couponScan.errorTitle'), errorMessage);
          }
        } catch (error) {
          this.showExceptionDialog(this.$t('couponScan.noteTitle'), this.$t('errorMessages.default'));
          console.error(error);
        }
      },
      showExceptionDialog(title, message) {
        this.exceptionDialog = true;
        this.exceptionTitle = title;
        this.exceptionMessage = message;
      },
      closeCongratsDialog() {
        this.congratsDialog = false;
      },
      restartQrCodeScanner() {
        this.exceptionDialog = false;
        this.$refs['qrCodeScanner'].tryAgain();
      },
      cancel() {
        this.$router.push({name: 'home'});
      }
    }
  }
</script>

<style scoped lang="scss">
  .validation-success,
  .validation-failure,
  .validation-pending {
    position: absolute;
    width: 100%;
    height: 100%;

    background-color: rgba(255, 255, 255, .8);
    text-align: center;
    font-weight: bold;
    font-size: 1.4rem;
    padding: 10px;

    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
  }
  .validation-success {
    color: green;
  }
  .validation-failure {
    color: red;
  }
  .qr-icon {
    filter: invert(100%);
    width: 26px;
    margin-right: 20px;
  }
  .v-app-bar {
    &::v-deep .v-toolbar__content {
      line-height: normal;
    }
  }
</style>
